import React, { useEffect, useRef } from "react";
import { Button, Form } from "react-bootstrap";

export const EMPTY_DRAFT = {
  editingId: null,
  title: "",
  description: "",
  ghLink: "",
  demoLink: "",
  isOrganizational: false,
  image: null,
};

// A draft counts as a pending change once anything is typed, chosen or being edited.
export function draftIsActive(d) {
  return Boolean(
    d.editingId || d.title || d.description || d.ghLink || d.demoLink || d.isOrganizational || d.image
  );
}

// Presentational: the draft and the delete queue live in AdminPhotos and are
// applied by its single Save button.
function AdminProjects({ projects, version, draft, setDraft, deletes, onToggleDelete, busy }) {
  const imageRef = useRef(null);

  // Clear the file input whenever the draft's image is reset (save, discard, edit).
  useEffect(() => {
    if (!draft.image && imageRef.current) imageRef.current.value = "";
  }, [draft.image]);

  const set = (key) => (e) =>
    setDraft({ ...draft, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const clearDraft = () => setDraft(EMPTY_DRAFT);

  const edit = (p) => {
    setDraft({
      editingId: p.id,
      title: p.title,
      description: p.description,
      ghLink: p.ghLink,
      demoLink: p.demoLink,
      isOrganizational: p.isOrganizational,
      image: null,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="mb-4" style={{ color: "var(--text-color)" }}>
      <h4>Projects</h4>
      <p>
        {draft.editingId
          ? "Editing a project. Leave the image empty to keep the current one."
          : "Fill in this form to add a project."}
      </p>
      <Form onSubmit={(e) => e.preventDefault()} style={{ maxWidth: 640 }}>
        <Form.Control
          className="mb-2"
          placeholder="Title"
          maxLength={120}
          value={draft.title}
          onChange={set("title")}
        />
        <Form.Control
          className="mb-2"
          as="textarea"
          rows={4}
          placeholder="Short description"
          maxLength={1500}
          value={draft.description}
          onChange={set("description")}
        />
        <Form.Check
          className="mb-2"
          type="checkbox"
          id="project-enterprise"
          label="Enterprise / company project (hides the GitHub link)"
          checked={draft.isOrganizational}
          onChange={set("isOrganizational")}
        />
        {!draft.isOrganizational && (
          <Form.Control
            className="mb-2"
            type="url"
            placeholder="GitHub link (optional)"
            value={draft.ghLink}
            onChange={set("ghLink")}
          />
        )}
        <Form.Control
          className="mb-2"
          type="url"
          placeholder="Demo link (optional)"
          value={draft.demoLink}
          onChange={set("demoLink")}
        />
        <Form.Control
          className="mb-2"
          type="file"
          accept="image/*"
          ref={imageRef}
          onChange={(e) => setDraft({ ...draft, image: e.target.files[0] || null })}
        />
        {draftIsActive(draft) && (
          <Button variant="outline-light" size="sm" onClick={clearDraft} disabled={busy}>
            Clear project form
          </Button>
        )}
      </Form>

      {projects.length > 0 && (
        <ul className="mt-3" style={{ listStyle: "none", padding: 0 }}>
          {projects.map((p) => {
            const deleted = deletes.has(p.id);
            return (
              <li
                key={p.id}
                className="d-flex align-items-center mb-2"
                style={{ gap: 12, opacity: deleted ? 0.5 : 1 }}
              >
                <img
                  src={`/api/projects/${p.id}/image?v=${p.updated}-${version}`}
                  alt=""
                  style={{ width: 64, height: 40, objectFit: "cover", borderRadius: 4 }}
                />
                <span style={{ flex: 1, textDecoration: deleted ? "line-through" : "none" }}>
                  {p.title}
                  {deleted && " (will be deleted)"}
                  {draft.editingId === p.id && " (editing)"}
                </span>
                {!deleted && (
                  <Button size="sm" disabled={busy} onClick={() => edit(p)}>
                    Edit
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={deleted ? "outline-light" : "danger"}
                  disabled={busy}
                  onClick={() => onToggleDelete(p.id)}
                >
                  {deleted ? "Undo delete" : "Delete"}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AdminProjects;
